import React, { useCallback, useMemo } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { updateViewTab } from '../../../redux/slices/entityViewSlice'
import { useTranslation } from 'react-i18next'
import { findEntityById } from '../../../utils/EntityUtils'
import { IEntityInstanceRelationsResponse } from '../../../services/apiInterfaces'
import { IRelation } from '../../DTStudio/interfaces'
import { getRelationNameByTargetEntityTemplateId } from '../../../utils/RelationUtils'
import { Applications } from '../../../constants/constants'

interface ViewEntityTabsProps {
    isIntransitShipmentPreview: boolean
    shouldDisplayGeneralReportsTab: boolean
    shouldDisplayMapViewTab?: boolean
    widgetView: { [key: string]: string }
    relatedEntitiesResponse: IEntityInstanceRelationsResponse
    entityTemplateRelations: IRelation[]
    enabledApplications: string[]
}

const ViewEntityTabs: React.FC<ViewEntityTabsProps> = ({
    isIntransitShipmentPreview,
    shouldDisplayGeneralReportsTab,
    widgetView,
    shouldDisplayMapViewTab = false,
    relatedEntitiesResponse,
    entityTemplateRelations,
    enabledApplications,
}) => {
    const dispatch = useAppDispatch()
    const navList = useAppSelector((state) => state.nav.navList)
    const isGuestUser =
        useAppSelector((state) => state.searchParams.isGuestUser) === 'true'
    const inTransitSettings = useAppSelector(
        (state) => state.inTransit.inTransitSettings,
    )
    const { insuranceEntityTemplateId, searatesEntityTemplateId } =
        inTransitSettings || {}

    const searatesRelationName = getRelationNameByTargetEntityTemplateId(
        entityTemplateRelations,
        searatesEntityTemplateId,
    )
    const searatesEntityInstances = useMemo(
        () => relatedEntitiesResponse?.relations?.[searatesRelationName],
        [relatedEntitiesResponse, searatesRelationName],
    )
    const displayInsuranceTab = useMemo(
        () =>
            (isGuestUser &&
                enabledApplications.includes(Applications.cargoInsurance.id)) ||
            !!findEntityById(navList, insuranceEntityTemplateId).entity,
        [navList, insuranceEntityTemplateId, isGuestUser, enabledApplications],
    )

    const displayCarbonEmissionTab = enabledApplications.includes(
        Applications.carbonEmission.id,
    )
    const displayMaritimeTab = useMemo(
        () =>
            ((isGuestUser &&
                enabledApplications.includes(
                    Applications.maritimeTracking.id,
                )) ||
                !!findEntityById(navList, searatesEntityTemplateId).entity) &&
            !!searatesEntityInstances,
        [
            navList,
            searatesEntityTemplateId,
            isGuestUser,
            searatesEntityInstances,
            enabledApplications,
        ],
    )

    const tabView = useAppSelector((state) => state.entityInstanceView.viewTab)
    const { t } = useTranslation()

    const onChangeView = useCallback(
        (view: string) => {
            dispatch(updateViewTab(view))
        },
        [dispatch],
    )

    const tabConfigs = useMemo(() => {
        const tabs: { key: string; label: string }[] = [
            {
                key: widgetView.GRAPH_APACHE_ECHART_VIEW,
                label: t(
                    isIntransitShipmentPreview ? 'shipmentView' : 'graphView',
                ),
            },
        ]

        if (shouldDisplayMapViewTab) {
            tabs.push({
                key: widgetView.MAP_VIEW,
                label: t('mapView'),
            })
        }

        if (isIntransitShipmentPreview) {
            tabs.push({
                key: widgetView.ANALYSIS_VIEW,
                label: t('analysis'),
            })

            if (displayMaritimeTab) {
                tabs.push({
                    key: widgetView.MARINE_REPORT_VIEW,
                    label: t('maritimeReport'),
                })
            }

            if (displayCarbonEmissionTab) {
                tabs.push({
                    key: widgetView.CARBON_REPORT_VIEW,
                    label: t('co2Report'),
                })
            }

            if (displayInsuranceTab) {
                tabs.push({
                    key: widgetView.INSURANCE_VIEW,
                    label: t('insurance'),
                })
            }
        }

        if (shouldDisplayGeneralReportsTab) {
            tabs.push({
                key: widgetView.GENERAL_REPORT_VIEW,
                label: t('pdfReport'),
            })
        }

        tabs.push({
            key: widgetView.ALERTS_VIEW,
            label: t('alerts'),
        })

        return tabs
    }, [
        displayCarbonEmissionTab,
        displayInsuranceTab,
        displayMaritimeTab,
        isIntransitShipmentPreview,
        shouldDisplayGeneralReportsTab,
        shouldDisplayMapViewTab,
        t,
        widgetView,
    ])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            {tabConfigs.map(({ key, label }) => {
                const isActive = tabView === key
                return (
                    <Button
                        key={key}
                        id={`view-entity-tab-${key.toLowerCase()}`}
                        onClick={() => onChangeView(key)}
                        sx={{
                            marginRight: '10px',
                            backgroundColor: isActive ? '#0099a8' : 'inherit',
                            '&:hover': {
                                backgroundColor: isActive
                                    ? '#00b3c4'
                                    : '#e0e0e0',
                            },
                            border: '1px solid rgba(0, 0, 0, 0.3)',
                            textTransform: 'none',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ color: isActive ? '#FFFFFF' : 'inherit' }}
                        >
                            {label}
                        </Typography>
                    </Button>
                )
            })}
        </Box>
    )
}

export default ViewEntityTabs
